import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, Square, RotateCcw, Zap, X, AlertTriangle, Trash2 } from "lucide-react";
import { useApp } from "@/context/AppStore";
import { api } from "@/lib/api";

export function AdminPanel({ open, onClose }) {
  const { sim, act } = useApp();
  const [anomalies, setAnomalies] = useState([]);

  useEffect(() => {
    api.anomalyTypes().then(setAnomalies).catch(() => {});
  }, []);

  const grouped = anomalies.reduce((acc, a) => {
    (acc[a.category] = acc[a.category] || []).push(a);
    return acc;
  }, {});

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div className="fixed inset-0 bg-black/60 z-40" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
          <motion.div
            className="fixed top-0 right-0 h-full w-full max-w-md panel z-50 overflow-y-auto"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 26, stiffness: 240 }}
            data-testid="admin-panel"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-border sticky top-0 bg-surface z-10">
              <div>
                <div className="eyebrow" style={{ color: "#fab20b" }}>Administrator</div>
                <div className="font-display font-bold uppercase tracking-tight text-ink">Simulation Control</div>
              </div>
              <button onClick={onClose} data-testid="admin-close" className="text-ink-soft hover:text-ink"><X size={20} /></button>
            </div>

            <div className="p-5 space-y-6">
              {/* lifecycle */}
              <div>
                <div className="eyebrow mb-2">Engine · <span style={{ color: sim.status === "running" ? "#33b76d" : sim.status === "paused" ? "#fab20b" : "#E63946" }}>{sim.status}</span></div>
                <div className="grid grid-cols-4 gap-2">
                  <CtrlBtn icon={<Play size={15} />} label="Start" onClick={act.start} testid="sim-start" color="#33b76d" />
                  <CtrlBtn icon={<Pause size={15} />} label="Pause" onClick={act.pause} testid="sim-pause" color="#fab20b" />
                  <CtrlBtn icon={<Play size={15} />} label="Resume" onClick={act.resume} testid="sim-resume" color="#33b76d" />
                  <CtrlBtn icon={<Square size={15} />} label="Stop" onClick={act.stop} testid="sim-stop" color="#E63946" />
                </div>
              </div>

              {/* active anomalies */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="eyebrow">Active Anomalies · {sim.active_anomalies.length}</div>
                  {sim.active_anomalies.length > 0 && (
                    <button onClick={act.clear} data-testid="clear-anomalies" className="text-[0.66rem] font-mono text-alert hover:underline flex items-center gap-1">
                      <Trash2 size={12} /> Clear
                    </button>
                  )}
                </div>
                {sim.active_anomalies.length === 0 ? (
                  <div className="text-[0.75rem] text-ink-soft panel-2 px-3 py-2">No anomalies injected — baseline operation.</div>
                ) : (
                  <div className="space-y-1.5">
                    {sim.active_anomalies.map((a) => (
                      <div key={a.instance_id} className="panel-2 px-3 py-2 border-l-2 flex items-center gap-2" style={{ borderLeftColor: "#E63946" }}>
                        <AlertTriangle size={13} className="text-alert" />
                        <span className="text-[0.75rem] text-ink">{a.label}</span>
                        <span className="ml-auto text-[0.62rem] font-mono text-ink-soft">{a.category}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* inject */}
              <div>
                <div className="eyebrow mb-2">Inject Anomaly</div>
                <div className="space-y-3">
                  {Object.entries(grouped).map(([cat, items]) => (
                    <div key={cat}>
                      <div className="text-[0.66rem] font-mono text-brand-green mb-1.5">{cat}</div>
                      <div className="grid grid-cols-1 gap-1.5">
                        {items.map((a) => (
                          <button key={a.id} onClick={() => act.inject(a.id)} data-testid={`inject-${a.id}`}
                            className="flex items-center gap-2 px-3 py-2 panel-2 text-left hover:border-alert/60 transition-colors group">
                            <Zap size={13} className="text-brand-gold group-hover:text-alert" />
                            <span className="text-[0.78rem] text-ink">{a.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* reset */}
              <div className="pt-2 border-t border-border space-y-2">
                <button onClick={act.reset} data-testid="sim-reset"
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 font-display font-bold uppercase tracking-wider text-sm"
                  style={{ background: "rgba(51,183,109,0.14)", color: "#33b76d", border: "1px solid #33b76d55" }}>
                  <RotateCcw size={15} /> Reset System
                </button>
                <button onClick={() => { if (window.confirm("Factory reset will wipe history and restore initial state. Continue?")) act.factoryReset(); }}
                  data-testid="factory-reset"
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 font-mono uppercase tracking-wider text-[0.7rem] text-ink-soft hover:text-alert transition-colors">
                  Factory Reset
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function CtrlBtn({ icon, label, onClick, testid, color }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="flex flex-col items-center gap-1 py-2.5 panel-2 hover:border-current transition-colors"
      style={{ color }}>
      {icon}
      <span className="text-[0.62rem] font-mono uppercase">{label}</span>
    </button>
  );
}
